/**
 * The ways the "Open app" button can reach the app, and the reader's saved
 * default among them. Pure logic: which methods exist, which can open now, and
 * which one the primary link follows. The DOM lives in `openAppGroup.ts`.
 *
 * The IPFS content address is the fallback everywhere. It is the default, it
 * is what a reader gets when storage fails, and it is what a saved method
 * becomes when that method cannot open (a local copy that stopped running, a
 * list with no public gateway, or a retired choice that an older visit
 * saved: "hosted", or the separate "gateway" choice that the IPFS content
 * address now covers).
 *
 * Storage calls are wrapped, as in `localCopyMemory.ts`: a private window or
 * blocked site data makes storage throw, and the page then just forgets.
 */
import { APP_ENTRY_ROUTE, LOCAL_COPY_ORIGIN } from './config'
import type { LocalCopyStorageSource } from './localCopyMemory'
import { buildNeutralReleaseUrl } from './neutralGateway'
import type { VersionManifest } from '../../../scripts/ipfs-release/schema'

/** The two methods, as stable identifiers. These are also the saved values. */
export type OpenAppMethodId = 'ipfs' | 'local'

/** The method the page uses when the reader has chosen none, or the choice cannot open. */
export const DEFAULT_OPEN_APP_METHOD: OpenAppMethodId = 'ipfs'

/** One method as the menu shows it. */
export interface OpenAppMethod {
  readonly id: OpenAppMethodId
  /** A short name. */
  readonly title: string
  /** One line that says what the method does. */
  readonly summary: string
  /** Where the method opens the app, or `null` when it cannot open now. */
  readonly href: string | null
  /** Why the method cannot open now. `null` when it can. */
  readonly unavailableHint: string | null
}

/** What decides the address of each method. */
export interface OpenAppContext {
  readonly manifest: VersionManifest
  /** The Content ID of the release the page shows. */
  readonly cid: string
  /** Whether the local-copy probe found a running copy. */
  readonly localCopyRunning: boolean
}

/** Every method in menu order, each with its address or the reason it has none. */
export const listOpenAppMethods = ({ manifest, cid, localCopyRunning }: OpenAppContext): OpenAppMethod[] => {
  return [
    {
      id: 'ipfs',
      title: 'IPFS content address',
      summary: 'A new address for each release, on a public IPFS gateway. Approvals and settings start fresh.',
      href: `${buildNeutralReleaseUrl(manifest, cid)}${APP_ENTRY_ROUTE}`,
      unavailableHint: null,
    },
    {
      id: 'local',
      title: 'Your own computer',
      summary: 'The release served by the Gibs program on this computer.',
      href: localCopyRunning ? `${LOCAL_COPY_ORIGIN}/${APP_ENTRY_ROUTE}` : null,
      unavailableHint: localCopyRunning
        ? null
        : 'Not running. Start the Gibs program, then check for your local copy.',
    },
  ]
}

const isMethodId = (value: unknown): value is OpenAppMethodId =>
  value === 'ipfs' || value === 'local'

/**
 * The method the primary link follows: the saved one when it can open now,
 * the IPFS content address otherwise.
 */
export const resolveEffectiveMethod = (
  methods: readonly OpenAppMethod[],
  savedId: OpenAppMethodId | null,
): OpenAppMethod & { readonly href: string } => {
  const candidates = methods.filter((method) => method.id === savedId || method.id === DEFAULT_OPEN_APP_METHOD)
  const saved = candidates.find((method) => method.id === savedId)
  const chosen = saved?.href === null ? undefined : saved
  const effective = chosen ?? candidates.find((method) => method.id === DEFAULT_OPEN_APP_METHOD)
  if (effective === undefined || effective.href === null) {
    throw new Error('The method list has no IPFS entry that can open.')
  }
  return { ...effective, href: effective.href }
}

const STORAGE_KEY = 'gibs-landing-open-app-method'

/** The saved method, or `null` when none is saved, the value is unknown, or storage fails. */
export const readSavedMethod = (storageSource: LocalCopyStorageSource): OpenAppMethodId | null => {
  try {
    const value = storageSource().getItem(STORAGE_KEY)
    return isMethodId(value) ? value : null
  } catch {
    return null
  }
}

/** Saves the reader's method. Does nothing when storage fails. */
export const saveMethod = (storageSource: LocalCopyStorageSource, id: OpenAppMethodId): void => {
  try {
    storageSource().setItem(STORAGE_KEY, id)
  } catch {
    // Storage unavailable: the choice holds for this visit only.
  }
}
